/**
 * Bubble-menu support plugin. The plugin's only job is to broadcast the
 * current selection's coordinates so the Vue overlay can position itself with
 * @floating-ui/vue. No DOM is touched here.
 *
 * Consumers pass an `onUpdate` callback that receives `{ visible, rect }`:
 *  - visible = true when there's a non-empty text selection
 *  - rect    = a DOMRect (or null) describing the union of the selection
 *              coordinates in viewport space.
 */
import { Plugin, PluginKey } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

export const bubbleMenuPluginKey = new PluginKey('pmBubbleMenu');

export interface BubbleState {
  visible: boolean
  rect: DOMRect | null
}

export function buildBubbleMenuPlugin(onUpdate: (state: BubbleState) => void) {
  let last: BubbleState = { visible: false, rect: null };

  const compute = (view: EditorView): BubbleState => {
    const { state } = view;
    const { from, to, empty } = state.selection;
    if (!view.hasFocus() || empty || from === to) {
      return { visible: false, rect: null };
    }
    // Skip when the selection is inside the code-block or a special node.
    const startCoords = view.coordsAtPos(from);
    const endCoords = view.coordsAtPos(to);
    const left = Math.min(startCoords.left, endCoords.left);
    const right = Math.max(startCoords.right, endCoords.right);
    const top = Math.min(startCoords.top, endCoords.top);
    const bottom = Math.max(startCoords.bottom, endCoords.bottom);
    const rect = new DOMRect(left, top, right - left, bottom - top);
    return { visible: true, rect };
  };

  return new Plugin({
    key: bubbleMenuPluginKey,
    view: (view) => {
      const handle = () => {
        const next = compute(view);
        if (next.visible !== last.visible || rectsDiffer(next.rect, last.rect)) {
          last = next;
          onUpdate(next);
        }
      };
      // Initial compute
      handle();
      const onScroll = () => handle();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onScroll);
      return {
        update() { handle(); },
        destroy() {
          window.removeEventListener('scroll', onScroll, true);
          window.removeEventListener('resize', onScroll);
          onUpdate({ visible: false, rect: null });
        },
      };
    },
  });
}

function rectsDiffer(a: DOMRect | null, b: DOMRect | null) {
  if (a === b) return false;
  if (!a || !b) return true;
  return a.left !== b.left || a.top !== b.top || a.width !== b.width || a.height !== b.height;
}
